import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { ControlDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StudioDevice } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { isEmpty, isNotNil, toNumber, toString } from '@tryiris-ai/js-types';
import type { Update } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Update an existing _audio_ device.
 *
 * @param deviceId - The ID of the audio device to be updated.
 * @param updates - Changes to be made to the audio device.
 * @param token - The token.
 */
export const update: Update = async (deviceId, updates, token) => {
  try {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      throw new Error('UA-018');
    }

    // Define mutation(s)
    const studio: DeepPartial<StudioDevice> = {};
    const control: DeepPartial<ControlDevice> = {};

    const name = toString(updates.name);

    if (name) {
      studio.name = name;
    }

    const offset = toNumber(updates.audio?.offset);

    if (isNotNil(offset)) {
      /* eslint-disable */
      /* @todo - port to new device settings
      control.audio = {
        // @fixme - waiting on Kevin - offset: wrapMutable(offset)
      };
      */
      /* eslint-enable */
    }

    // Are there any changes to be made?
    if (isEmpty(studio) && isEmpty(control)) {
      return;
    }

    await proxy.mutate(
      {
        control: {
          ...(!isEmpty(control) // TODO clean: we don't need to check the truthiness of the object, due to the check above
            ? {
                device: {
                  [deviceId]: control
                }
              }
            : {}),
          ...(!isEmpty(studio) // same as above
            ? {
                studio: {
                  device: {
                    [deviceId]: studio
                  }
                }
              }
            : {})
        }
      },
      {}
    );

    await proxy.save(token);
  } catch (cause) {
    throw new StudioError(`Unable to update audio device: ${deviceId}`, { cause });
  }
};
